import React, { useRef, useCallback, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    intl,
    DmsAltBox,
    DmsPage,
    DmsTable,
    DmsTabber,
    DmsButton,
    DmsFlexContainer,
    DmsPermissions,
    LanguageBaseContext,
} from "dmsbasereact";
import { Typography, Chip, IconButton, Tooltip, Box } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Permissions } from "../../constants/Permissions";
import { APP_ROUTES } from "../../utils/NamedRoutes";
import { STATUS_COLORS, FINAL_STATUSES } from "./constants";
import { CreateNoticeModal } from "./CreateNoticeModal";

export const SystemNoticePage = () => {
    const tablePendingRef = useRef(null);
    const tableResolvedRef = useRef(null);
    const tableArchivedRef = useRef(null);
    const navigate = useNavigate();
    const { language } = useContext(LanguageBaseContext);
    const [createModalOpen, setCreateModalOpen] = useState(false);

    const typeLabels = useMemo(() => ({
        1: intl.get("DispensationWithoutStock").d("Dispensation Without Stock"),
        2: intl.get("CheckinWithoutCheckout").d("Checkin Without Checkout"),
        3: intl.get("CheckinDifferentFromInvoice").d("Checkin Different From Invoice"),
        4: intl.get("NoCheckoutForPeriod").d("No Checkout For Period"),
        5: intl.get("StockAdjustment").d("Stock Adjustment"),
        6: intl.get("CheckoutWithoutBalance").d("Checkout Without Balance"),
        7: intl.get("UnknownDestinationInstitution").d("Unknown Destination Institution"),
    }), [language]);

    const statusLabels = useMemo(() => ({
        1: intl.get("Pending").d("Pending"),
        2: intl.get("InProgress").d("In Progress"),
        3: intl.get("AwaitingJustification").d("Awaiting Justification"),
        4: intl.get("EurofarmaValidation").d("Eurofarma Validation"),
        5: intl.get("Resolved").d("Resolved"),
        6: intl.get("Archived").d("Archived"),
    }), [language]);

    const getTypeName = useCallback((value) => typeLabels[value] || "-", [typeLabels]);

    const getStatusName = useCallback((value) => statusLabels[value] || "-", [statusLabels]);

    const typeOptions = useMemo(() =>
        Object.entries(typeLabels).map(([value, label]) => ({ value: Number(value), label })),
    [typeLabels]);

    const pendingStatusOptions = useMemo(() =>
        Object.entries(statusLabels)
            .filter(([value]) => !FINAL_STATUSES.includes(Number(value)))
            .map(([value, label]) => ({ value: Number(value), label })),
    [statusLabels]);

    const handleNavigateToDetail = useCallback((row) => {
        navigate(`${APP_ROUTES.SystemNotice}/${row.pk}`, {
            state: {
                noticeCode: row.code,
                institutionName: row.institution?.name || null,
                userName: row.user?.user_name || intl.get("SystemNotice").d("System Notice"),
                institutionEmail: row.institution?.email_address || null,
                institutionPhone: row.institution?.phone_number || null,
                userEmail: row.user?.user_email || null,
                userPhone: row.user?.user_phone || null,
            },
        });
    }, [navigate]);

    const handleNoticeSaved = useCallback(() => {
        setCreateModalOpen(false);
        tablePendingRef.current?.updateTable?.();
    }, []);

    const buildHeader = useCallback((statusOptions = null) => [
        {
            label: intl.get("NoticeCode").d("Code"),
            column_name: "code",
            filter: true,
            element: (row) => (
                <Typography fontWeight="500">{row.code}</Typography>
            ),
        },
        {
            label: intl.get("NoticeType").d("Type"),
            column_name: "notice_type",
            filter: true,
            control: "select",
            options: typeOptions,
            sortable: true,
            element: (row) => {
                const value = row.notice_type?.value !== undefined ? row.notice_type.value : row.notice_type;
                return <Chip label={getTypeName(value)} size="small" variant="outlined" />;
            },
        },
        {
            label: intl.get("NoticeStatus").d("Status"),
            column_name: "status",
            filter: !!statusOptions,
            ...(statusOptions ? { control: "select", options: statusOptions } : {}),
            sortable: true,
            element: (row) => {
                const value = row.status?.value !== undefined ? row.status.value : row.status;
                return (
                    <Chip
                        label={getStatusName(value)}
                        size="small"
                        color={STATUS_COLORS[value] || "default"}
                    />
                );
            },
        },
        {
            label: intl.get("NoticeInstitution").d("Institution"),
            column_name: "institution",
            filter: true,
            sortable: false,
            element: (row) => row.institution?.name || "-",
        },
        {
            label: intl.get("NoticeUser").d("User"),
            column_name: "user",
            filter: false,
            sortable: false,
            element: (row) => row.user?.user_name || intl.get("SystemNotice").d("System Notice"),
        },
        {
            label: intl.get("Actions").d("Actions"),
            column_name: "actions",
            width: "120px",
            sortable: false,
            filter: false,
            element: (row) => (
                <DmsPermissions permissions={[Permissions.NOTICE_MANAGE]}>
                    <Tooltip title={intl.get("ViewAnnotations").d("View Annotations")}>
                        <IconButton
                            size="small"
                            onClick={() => handleNavigateToDetail(row)}
                            color="primary"
                        >
                            <VisibilityIcon />
                        </IconButton>
                    </Tooltip>
                </DmsPermissions>
            ),
        },
    ], [typeOptions, pendingStatusOptions, getTypeName, getStatusName, handleNavigateToDetail]);

    const headerPending = useMemo(() => buildHeader(pendingStatusOptions), [buildHeader, pendingStatusOptions]);
    const headerNoStatusFilter = useMemo(() => buildHeader(null), [buildHeader]);

    const toolbar = (
        <DmsFlexContainer>
            <DmsFlexContainer.Left>
                <DmsPermissions permissions={[Permissions.NOTICE_MANAGE]}>
                    <DmsButton
                        variant="primary"
                        text={intl.get("CreateNotice").d("Create Notice")}
                        onClick={() => setCreateModalOpen(true)}
                    />
                </DmsPermissions>
            </DmsFlexContainer.Left>
        </DmsFlexContainer>
    );

    const tabs = [
        {
            title: intl.get("PendingNotices").d("Pending"),
            component: (
                <DmsTable
                    ref={tablePendingRef}
                    url="/system_notice/table"
                    header={headerPending}
                    managed={true}
                    needs_authentication={true}
                    pre_filter={[{ name: "status_in", value: "1,2,3,4" }]}
                    export_table={false}
                    toolbar={toolbar}
                />
            ),
        },
        {
            title: intl.get("ResolvedNotices").d("Resolved"),
            component: (
                <DmsTable
                    ref={tableResolvedRef}
                    url="/system_notice/table"
                    header={headerNoStatusFilter}
                    managed={true}
                    needs_authentication={true}
                    pre_filter={[{ name: "status_in", value: "5" }]}
                    export_table={false}
                    toolbar={toolbar}
                />
            ),
        },
        {
            title: intl.get("ArchivedNotices").d("Archived"),
            component: (
                <DmsTable
                    ref={tableArchivedRef}
                    url="/system_notice/table"
                    header={headerNoStatusFilter}
                    managed={true}
                    needs_authentication={true}
                    pre_filter={[{ name: "status_in", value: "6" }]}
                    export_table={false}
                    toolbar={toolbar}
                />
            ),
        },
    ];

    return (
        <DmsPage name="SystemNotices">
            <DmsAltBox.Card>
                <DmsAltBox.Header>
                    <Typography variant="h3" sx={{ fontSize: "1.1rem" }}>
                        {intl.get("SystemNotices").d("Notices")}
                    </Typography>
                </DmsAltBox.Header>
                <DmsAltBox.Body>
                    <DmsTabber tabs={tabs} pageName={intl.get("SystemNotices").d("Notices")} />
                </DmsAltBox.Body>
            </DmsAltBox.Card>

            <CreateNoticeModal
                open={createModalOpen}
                onClose={() => setCreateModalOpen(false)}
                onSaved={handleNoticeSaved}
            />
        </DmsPage>
    );
};

export default SystemNoticePage;
